import { useState } from 'react'
import { Badge, EmptyState, ErrorState, Modal, Skeleton } from '@indipia/ui-core'
import type { WorkOrderItem } from '../../api/taskProgressApi'
import { useTaskProgressDetail } from '../../hooks/useTaskProgress'
import { formatMonthDayTime } from '../PartnerCandidateItem'
import { PhotoCountLink } from './PhotoCountLink'
import { DeliveredCustReqList } from './DeliveredCustReqList'

// 시공 세부 내역 팝업 — 작업요청(B-09). 작업요청 카드: 공종 칩·작업자·참조인·발송 시각·본문·사진.
// 데이터는 GET /schedule/tasks/{taskId}/progress-detail. 카드 아래에 전달된 고객 요청 n건 목록(941 V1.4 S16-4, 응답 customerRequests)을 둔다. 필드가 없거나 비면 숨긴다.

interface Props {
  open: boolean
  onClose: () => void
  taskId: number
  /** 머리 부제 — 공종명 */
  workTypeName: string
  /** 머리 부제 — 지정된 시공파트너 이름(없으면 생략) */
  partnerName?: string
}

const caption: React.CSSProperties = { fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }

function names(item: WorkOrderItem, role: string): string {
  return item.recipients.filter(r => r.role === role).map(r => r.partnerName).filter(Boolean).join(', ')
}

export function WorkOrderDetailModal({ open, onClose, taskId, workTypeName, partnerName }: Props) {
  const q = useTaskProgressDetail(taskId, open)
  // 사진 뷰어가 열려 있는 동안 Esc·바깥 클릭은 뷰어만 닫는다
  const [viewerOpen, setViewerOpen] = useState(false)
  const subtitle = [workTypeName, partnerName].filter(Boolean).join(' · ')
  const orders = q.data?.workOrders ?? []

  return (
    <Modal open={open} onClose={() => { if (!viewerOpen) onClose() }} title="작업요청" size="md">
      <div onMouseDown={e => e.stopPropagation()}>
        <div style={caption}>시공파트너 진행 내역</div>
        {subtitle && <div className="text-sm font-bold" style={{ color: 'var(--shell-on-surface)' }}>{subtitle}</div>}

        {q.isLoading && (
          <div aria-busy="true" className="mt-2 flex flex-col gap-2">
            {[0, 1, 2].map(i => <Skeleton key={i} height={28} />)}
          </div>
        )}
        {q.isError && <div className="mt-2"><ErrorState title="작업요청을 불러오지 못했어요" onRetry={() => { void q.refetch() }} /></div>}
        {q.data && orders.length === 0 && <div className="mt-2"><EmptyState title="보낸 작업요청이 없어요" /></div>}

        {orders.length > 0 && (
          <ul className="mt-2 flex flex-col gap-2 list-none p-0 m-0">
            {orders.map(o => {
              const workers = names(o, 'WORKER')
              const refs = names(o, 'CC')
              const sent = formatMonthDayTime(o.sentAt)
              return (
                <li key={o.workOrderId} className="rounded-xl p-3" style={{ border: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-surface)' }}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge status="info">{o.workTypeName ?? workTypeName}</Badge>
                    {sent && <span style={caption}>{`${sent} 발송`}</span>}
                  </div>
                  {(workers || refs) && (
                    <div className="mt-1.5" style={caption}>
                      {workers && <span>{`작업자 ${workers}`}</span>}
                      {workers && refs && <span aria-hidden="true">{' · '}</span>}
                      {refs && <span>{`참조인 ${refs}`}</span>}
                    </div>
                  )}
                  <div className="mt-1.5 text-sm" style={{ color: 'var(--shell-on-surface)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {o.orderContent?.trim() ? o.orderContent : '-'}
                  </div>
                  <div className="mt-1.5 text-xs">
                    <PhotoCountLink photos={o.photos} count={o.photoCount} title="작업요청 사진" alt="작업요청 사진" onViewerOpenChange={setViewerOpen} />
                  </div>
                  <DeliveredCustReqList items={o.customerRequests} onViewerOpenChange={setViewerOpen} />
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </Modal>
  )
}
